import { apiClient } from './apiClient';
import {
  AuditListParams,
  AuditListResponse,
  AuditSummaryParams,
  AuditSummaryResponse,
} from '../types/audit';

class AuditService {
  /**
   * List audit log entries with optional filters.
   * Calls GET /v2/audit-log. Admin-only on the backend.
   */
  async listAuditLog(
    params: AuditListParams = {},
    opts?: { signal?: AbortSignal },
  ): Promise<AuditListResponse> {
    const query = new URLSearchParams();
    if (params.resource_type) query.set('resource_type', params.resource_type);
    if (params.resource_id) query.set('resource_id', params.resource_id);
    if (params.action) query.set('action', params.action);
    if (params.actor_username) query.set('actor_username', params.actor_username);
    if (params.status) query.set('status', params.status);
    if (params.from_timestamp) query.set('from_timestamp', params.from_timestamp);
    if (params.to_timestamp) query.set('to_timestamp', params.to_timestamp);
    if (params.exclude_reads) query.set('exclude_reads', 'true');
    if (params.authenticated_only !== undefined)
      query.set(
        'authenticated_only',
        params.authenticated_only ? 'true' : 'false',
      );
    if (typeof params.page === 'number') query.set('page', String(params.page));
    if (typeof params.page_size === 'number') query.set('page_size', String(params.page_size));

    const qs = query.toString();
    const url = qs ? `/v2/audit-log?${qs}` : '/v2/audit-log';
    return await apiClient.get(url, opts);
  }

  /**
   * Aggregate dashboard data — KPI tiles, top actors/actions, timeline,
   * recent failures. Calls GET /v2/audit-log/summary. Admin-only.
   */
  async getAuditSummary(
    params: AuditSummaryParams = {},
    opts?: { signal?: AbortSignal },
  ): Promise<AuditSummaryResponse> {
    const query = new URLSearchParams();
    if (params.from_timestamp) query.set('from_timestamp', params.from_timestamp);
    if (params.to_timestamp) query.set('to_timestamp', params.to_timestamp);
    if (params.exclude_reads !== undefined)
      query.set('exclude_reads', params.exclude_reads ? 'true' : 'false');
    if (params.authenticated_only !== undefined)
      query.set(
        'authenticated_only',
        params.authenticated_only ? 'true' : 'false',
      );
    if (params.bucket) query.set('bucket', params.bucket);
    if (typeof params.top_n === 'number') query.set('top_n', String(params.top_n));
    if (typeof params.failed_limit === 'number')
      query.set('failed_limit', String(params.failed_limit));

    const qs = query.toString();
    const url = qs ? `/v2/audit-log/summary?${qs}` : '/v2/audit-log/summary';
    return await apiClient.get(url, opts);
  }
}

export const auditService = new AuditService();
